import { useContext, useEffect, useMemo, useState } from "react";
import Calendar from "react-calendar";
import styles from "./Calendar.module.scss";
import "./Calendar.scss";
import {
  createMonthlyDistinctsBatch,
  createMonthlyEchoesBatch,
  createWorklog,
  deleteWorklog,
  fetchHasDistincts,
  fetchHasEchoes,
  fetchMonthlyDistincts,
  fetchMonthlyEchoes,
  fetchUserWorklogs,
  patchMonthlyDistincts,
  patchMonthlyEchoes,
  patchWorklog,
} from "../../api";
import { AppContext } from "../../AppContext";
import { toDjangoDate } from "../../utils/dbUtils";
import { images_path } from "../../constants";
import CustomAutocomplete from "../CustomAutocomplete";
import { useQuery, useMutation } from "@tanstack/react-query";

export const CalendarComponent = ({ updateTaskList }) => {
  const { user, tasks } = useContext(AppContext);

  const [activeDate, setActiveDate] = useState(new Date());
  const [activeStartDate, setActiveStartDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [worklogs, setWorklogs] = useState([]);
  const [currentLogs, setCurrentLogs] = useState([]);
  const { workTypes } = useContext(AppContext);

  const [distinctsNum, setDistinctsNum] = useState(0);
  const [echoesNum, setEchoesNum] = useState(0);

  const hasDistinctsQuery = useQuery({
    queryKey: ["has-distincts"],
    queryFn: fetchHasDistincts,
  });

  const hasEchoesQuery = useQuery({
    queryKey: ["has-echoes"],
    queryFn: fetchHasEchoes,
  });

  const monthlyDistinctsQuery = useQuery({
    queryKey: ["monthly-distincts", activeStartDate],
    queryFn: () => {
      return fetchMonthlyDistincts({
        user: user.id,
        month: activeStartDate.getMonth() + 1,
        year: activeStartDate.getFullYear(),
      });
    },
  });

  const monthlyEchoesQuery = useQuery({
    queryKey: ["monthly-echoes", activeStartDate],
    queryFn: () => {
      return fetchMonthlyEchoes({
        user: user.id,
        month: activeStartDate.getMonth() + 1,
        year: activeStartDate.getFullYear(),
      });
    },
  });

  const currentMonthlyDistincts = useMemo(() => {
    if (monthlyDistinctsQuery.data && monthlyDistinctsQuery.data.length > 0) {
      return monthlyDistinctsQuery.data[0];
    }
    return null;
  }, [monthlyDistinctsQuery.data]);

  const currentMonthlyEchoes = useMemo(() => {
    if (monthlyEchoesQuery.data && monthlyEchoesQuery.data.length > 0) {
      return monthlyEchoesQuery.data[0];
    }
    return null;
  }, [monthlyEchoesQuery.data]);

  useEffect(() => {
    if (currentMonthlyDistincts) {
      setDistinctsNum(currentMonthlyDistincts.num_of_distincts);
    } else {
      setDistinctsNum(0);
    }
    if (currentMonthlyEchoes) {
      setEchoesNum(currentMonthlyEchoes.num_of_echoes);
    } else {
      setEchoesNum(0);
    }
  }, [currentMonthlyDistincts, currentMonthlyEchoes, activeStartDate]);

  const createMonthlyDistinctsMutation = useMutation({
    mutationFn: (num) =>
      createMonthlyDistinctsBatch({
        user: user.id,
        month: activeStartDate.getMonth() + 1,
        year: activeStartDate.getFullYear(),
        num_of_distincts: num,
      }),
  });

  const createMonthlyEchoesMutation = useMutation({
    mutationFn: (num) =>
      createMonthlyEchoesBatch({
        user: user.id,
        month: activeStartDate.getMonth() + 1,
        year: activeStartDate.getFullYear(),
        num_of_echoes: num,
      }),
  });

  const patchMonthlyDistinctsMutation = useMutation({
    mutationFn: (num) =>
      patchMonthlyDistincts(currentMonthlyDistincts.id, {
        month: activeStartDate.getMonth() + 1,
        year: activeStartDate.getFullYear(),
        num_of_distincts: num,
      }),
  });

  const patchMonthlyEchoesMutation = useMutation({
    mutationFn: (num) =>
      patchMonthlyEchoes(currentMonthlyEchoes.id, {
        month: activeStartDate.getMonth() + 1,
        year: activeStartDate.getFullYear(),
        num_of_echoes: num,
      }),
  });

  const anyMutationIsPending = () => {
    return (
      createMonthlyDistinctsMutation.isPending ||
      createMonthlyEchoesMutation.isPending ||
      patchMonthlyDistinctsMutation.isPending ||
      patchMonthlyEchoesMutation.isPending
    );
  };

  const anyMutationIsError = () => {
    return (
      createMonthlyDistinctsMutation.isError ||
      createMonthlyEchoesMutation.isError ||
      patchMonthlyDistinctsMutation.isError ||
      patchMonthlyEchoesMutation.isError
    );
  };

  const handleChangeDistincts = (num) => {
    if (currentMonthlyDistincts) {
      patchMonthlyDistinctsMutation.mutate(num);
    } else {
      createMonthlyDistinctsMutation.mutate(num);
    }
  };
  const handleChangeEchoes = (num) => {
    if (currentMonthlyEchoes) {
      patchMonthlyEchoesMutation.mutate(num);
    } else {
      createMonthlyEchoesMutation.mutate(num);
    }
  };

  const worklogsStartDate = useMemo(() => {
    if (!activeStartDate) return;

    const date = new Date(activeStartDate);
    date.setDate(date.getDate() - 7);
    return date;
  }, [activeStartDate]);

  const worklogsEndDate = useMemo(() => {
    if (!activeStartDate) return;

    const date = new Date(activeStartDate);
    date.setMonth(date.getMonth() + 1, 0);
    date.setDate(date.getDate() + 7);
    return date;
  }, [activeStartDate]);

  const fetchActiveWorklogsQuery = useQuery({
    queryKey: ["active-logs", activeDate],
    queryFn: () => {
      return fetchUserWorklogs({ date: toDjangoDate(activeDate) });
    },
  });

  const fetchWorklogsQuery = useQuery({
    queryKey: ["logs", worklogsStartDate],
    queryFn: () => {
      return fetchUserWorklogs({
        start_date: toDjangoDate(worklogsStartDate),
        end_date: toDjangoDate(worklogsEndDate),
      });
    },
  });

  const createWorklogMutation = useMutation({
    mutationFn: (data) => {
      return createWorklog(data);
    },
    onSuccess: () => {
      loadActiveWorklogs();
    },
  });

  const patchWorklogMutation = useMutation({
    mutationFn: ({ id, data }) => {
      return patchWorklog(id, data);
    },
    onSuccess: () => {
      loadActiveWorklogs();
      updateTaskList();
    },
  });

  const deleteWorklogMutation = useMutation({
    mutationFn: ({ id }) => {
      return deleteWorklog(id);
    },
    onSuccess: () => {
      loadActiveWorklogs();
      updateTaskList();
    },
  });

  useEffect(() => {
    const data = fetchActiveWorklogsQuery.data;
    if (data) {
      setCurrentLogs(data);
      // console.log(`Effected: ${JSON.stringify(data)}`);
      setWorklogs([
        ...data,
        ...worklogs.filter((log) => log.date !== toDjangoDate(activeDate)),
      ]);
    }
  }, [fetchActiveWorklogsQuery.data]);

  useEffect(() => {
    const data = fetchWorklogsQuery.data;
    if (data) {
      setWorklogs(data);
    }
  }, [fetchWorklogsQuery.data]);

  const getFullDate = (date) => {
    return (
      date.getDate().toString() +
      date.getMonth().toString() +
      date.getFullYear().toString()
    );
  };

  const handleWorkTypeChange = (e, worklog) => {
    if (!worklog) {
      createWorklogMutation.mutate({
        date: toDjangoDate(activeDate),
        work_type_id: e.target.value,
      });
    } else {
      patchWorklogMutation.mutate({
        id: worklog.id,
        data: {
          work_type_id: e.target.value,
        },
      });
    }
  };

  const getTaskField = (worklog, workTypeId) => {
    if (
      workTypes.find((workType) => workType.id === workTypeId)?.name ===
      "Commessa"
    ) {
      if (!worklog.task) {
        return (
          <CustomAutocomplete
            options={tasks.filter(
              (task) =>
                task.name &&
                task.assigned_users.some(
                  (taskUser) => user.id === taskUser.id,
                ) &&
                task.status.toLowerCase() === "open",
            )}
            optionLabel={(task) => {
              return task.name;
            }}
            optionDisplay={(task) => {
              return (
                <div className={styles.autocomplete_option}>{task.name}</div>
              );
            }}
            maxDisplayOptions={5}
            width="10.5rem"
            onSelect={(newValue) => {
              patchWorklogMutation.mutate({
                id: worklog.id,
                data: { task_id: newValue.id },
              });
            }}
          />
        );
      } else {
        return (
          <div className={styles.task_container}>
            {worklog.task.name}
            <button
              className={styles.edit_icon}
              onClick={() => {
                patchWorklogMutation.mutate({
                  id: worklog.id,
                  data: { task_id: null },
                });
              }}
            >
              <img src={images_path + "edit.svg"}></img>
            </button>
          </div>
        );
      }
    }
    return null;
  };

  const getLog = (worklog) => {
    let workTypeId = worklog ? worklog.work_type.id : -1;
    return (
      <div key={worklog ? worklog.id : -1} className={styles.log_container}>
        {worklog && (
          <button
            className={styles.log_close_icon}
            onClick={() => {
              if (worklog) {
                deleteWorklogMutation.mutate({ id: worklog.id });
              }
            }}
          >
            <img src={images_path + "x_thin.svg"}></img>
          </button>
        )}
        <select
          className={styles.select}
          value={workTypeId}
          onChange={(e) => handleWorkTypeChange(e, worklog)}
        >
          {!worklog && <option value={-1}>---</option>}
          {workTypes.map((workType, idx) => (
            <option key={workType.id} value={workType.id}>
              {workType.name}
            </option>
          ))}
        </select>
        {getTaskField(worklog, workTypeId)}
        {workTypeId != -1 &&
        !(worklog.work_type.name == "Commessa" && !worklog.task) ? (
          <div className={styles.hours_worked_container}>
            <label htmlFor="hours_worked">Ore lavorate:</label>
            <input
              id="hours_worked"
              type="number"
              min="0"
              max="1000"
              defaultValue={worklog.hours_worked}
              onChange={(e) => {
                let value = e.target.value;
                if (!value) value = 0;
                patchWorklogMutation.mutate({
                  id: worklog.id,
                  data: {
                    hours_worked: value,
                  },
                });
              }}
              style={{ maxWidth: "5rem" }}
            ></input>
          </div>
        ) : null}
        {worklog && (
          <div
            className={styles.hours_worked_container}
            style={{ alignItems: "flex-start" }}
          >
            <label htmlFor="notes">Note:</label>
            <textarea
              id="notes"
              type="text"
              defaultValue={worklog.notes}
              onChange={(e) => {
                patchWorklogMutation.mutate({
                  id: worklog.id,
                  data: {
                    notes: e.target.value,
                  },
                });
              }}
              // style={{ maxWidth: "5rem" }}
            ></textarea>
          </div>
        )}
      </div>
    );
  };

  const getTileColorClass = (date) => {
    let filteredLogs = [];
    if (toDjangoDate(date) === toDjangoDate(activeDate)) {
      filteredLogs = currentLogs;
    } else {
      filteredLogs = worklogs.filter(
        (worklog) => worklog.date === toDjangoDate(date),
      );
    }

    let hours_worked = 0;
    const baseClassName = styles.tile_bg + " ";
    let className = baseClassName;
    filteredLogs.map((log) => {
      hours_worked += log.hours_worked;
      if (
        className === baseClassName &&
        log.work_type.name === "Ferie/Permessi" &&
        log.hours_worked > 0
      ) {
        className += styles.vacations + " ";
      }
    });
    className += styles.tile_bg + " ";
    if (hours_worked === 8) {
      className += styles.complete;
    } else if (hours_worked > 8) {
      className += styles.overtime;
    } else if (hours_worked > 0 && hours_worked < 8) {
      className += styles.incomplete;
    }
    return className;
  };

  const loadActiveWorklogs = () => {
    fetchActiveWorklogsQuery.refetch();
  };

  const loadWorklogs = () => {
    fetchWorklogsQuery.refetch();
  };

  const getTotalHours = () => {
    return currentLogs.reduce((acc, worklog) => {
      return acc + worklog.hours_worked;
    }, 0);
  };

  return (
    <div className={styles.container}>
      <div>
        <Calendar
          value={activeDate}
          activeStartDate={activeStartDate}
          onActiveStartDateChange={(info) => {
            setActiveStartDate(info.activeStartDate);
          }}
          onClickDay={setActiveDate}
          tileContent={({ date, view }) => (
            <>
              {<div className={getTileColorClass(date)}></div>}
              <div className={styles.tile_content}>
                {getFullDate(date) === getFullDate(activeDate) ? (
                  <div className={styles.active_symbol}></div>
                ) : null}
              </div>
            </>
          )}
        />
        <div style={{ marginTop: ".5rem", display: "flex", gap: "1rem" }}>
          {hasDistinctsQuery.data && (
            <div
              style={{
                display: "flex",
                gap: ".25rem",
                alignItems: "center",
              }}
            >
              <label>N. distinte:</label>
              <input
                type="number"
                style={{ maxWidth: "4rem" }}
                value={distinctsNum}
                onChange={(e) => setDistinctsNum(e.target.value)}
              ></input>
            </div>
          )}
          {hasEchoesQuery.data && (
            <div
              style={{ display: "flex", gap: ".25rem", alignItems: "center" }}
            >
              <label>N. eco:</label>
              <input
                type="number"
                style={{ maxWidth: "4rem" }}
                value={echoesNum}
                onChange={(e) => setEchoesNum(e.target.value)}
              ></input>
            </div>
          )}
          {hasDistinctsQuery.data || hasEchoesQuery.data ? (
            <button
              className={`button_var1 ${anyMutationIsPending() && "disabled"}`}
              onClick={() => {
                if (!anyMutationIsPending()) {
                  if (hasDistinctsQuery.data)
                    handleChangeDistincts(distinctsNum);
                  if (hasEchoesQuery.data) handleChangeEchoes(echoesNum);
                }
              }}
            >
              {anyMutationIsPending() ? "Salvando..." : "Salva"}
            </button>
          ) : null}
        </div>
        {anyMutationIsError() && (
          <p style={{ color: "red" }}>
            C'è stato un errore durante il salvataggio delle distinte/eco.
          </p>
        )}
      </div>
      {!fetchWorklogsQuery.isPending ? (
        <div className={styles.logs_container_wrapper}>
          <div className={styles.logs_container}>
            {currentLogs.map((worklog) => {
              return getLog(worklog);
            })}
            {getLog()}
          </div>
          <div className={styles.tot_hours}>
            {`Ore totali: ${getTotalHours()}`}
          </div>
        </div>
      ) : (
        <div style={{ textWrap: "nowrap" }}>Caricando worklogs...</div>
      )}
    </div>
  );
};
